import {createContext, type ReactNode, type RefObject, use} from 'react'

export interface ControlledContentPreviewExpansionProps {
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => void
}

export type ContentPreviewExpansionProps =
  | ControlledContentPreviewExpansionProps
  | {
      isExpanded?: never
      onExpandedChange?: never
    }

export function hasControlledContentPreviewExpansion(
  props: ContentPreviewExpansionProps,
): props is ControlledContentPreviewExpansionProps {
  return props.isExpanded !== undefined && props.onExpandedChange !== undefined
}

export interface ContentPreviewBaseProps {
  returnFocusRef: RefObject<HTMLElement | null>
  disableResourcePanel?: boolean
  resourcePanelSupportedOverride?: boolean
  disableActionsMenu?: boolean
  hideExpandButton?: boolean
  hideIcebreakerSuggestion?: boolean
  className?: string
}

export type ContentPreviewComponentProps = ContentPreviewBaseProps & ContentPreviewExpansionProps

type ContentPreviewRenderer = (props: ContentPreviewComponentProps) => ReactNode

/**
 * Provides a render function for ContentPreview via context to avoid circular module dependencies.
 * Consumers (e.g. TaskChat in agent-sessions) use ContentPreviewSlot instead of
 * importing ContentPreview directly.
 */
export const ContentPreviewRendererContext = createContext<ContentPreviewRenderer | null>(null)

/**
 * Stable wrapper component that renders ContentPreview from context.
 * Uses a render function pattern to avoid React Compiler's
 * "Cannot create components during render" error.
 */
export function ContentPreviewSlot(props: ContentPreviewComponentProps) {
  const {
    returnFocusRef,
    disableResourcePanel,
    resourcePanelSupportedOverride,
    disableActionsMenu,
    hideExpandButton,
    hideIcebreakerSuggestion,
    className,
    ...expansionProps
  } = props
  const renderer = use(ContentPreviewRendererContext)
  if (!renderer) return null
  return renderer({
    returnFocusRef,
    disableResourcePanel,
    resourcePanelSupportedOverride,
    disableActionsMenu,
    hideExpandButton,
    hideIcebreakerSuggestion,
    className,
    ...expansionProps,
  })
}
